import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LandingPage from './components/LandingPage';
import CurrencyEditor from './components/CurrencyEditor';
import { getCurrencyTypeInfo, parseCurrencyToEditorFormat } from './data/currencyExamples';
import allCurrencies from './allcurrencies.json';
import importedCurrencies from './importedcurrencies.json';
import './App.css';

// Map type ids to currencyType objects for the editor
const currencyTypeMap = {
  token: {
    id: 'token',
    title: 'Simple Token',
    color: 'from-blue-500 to-indigo-600',
  },
  basket: {
    id: 'basket',
    title: 'Liquidity Pool',
    color: 'from-emerald-500 to-teal-600',
  },
  pbaas: {
    id: 'pbaas',
    title: 'PBaaS Chain',
    color: 'from-purple-500 to-pink-600',
  },
  erc20: {
    id: 'erc20',
    title: 'Mapped ERC-20',
    color: 'from-orange-500 to-red-600',
  },
  gateway: {
    id: 'gateway',
    title: 'Gateway',
    color: 'from-cyan-500 to-blue-600',
  },
};

function App() {
  const [selectedType, setSelectedType] = useState(null);
  const [preloadedData, setPreloadedData] = useState(null);
  const [viewMode, setViewMode] = useState(false); // true = viewing existing, false = creating

  // Handle loading an example currency
  const handleLoadExample = (example) => {
    const typeObj = currencyTypeMap[example.type] || currencyTypeMap.token;
    setSelectedType(typeObj);
    setPreloadedData({
      isExample: true,
      config: {
        ...example.config,
        nodes: example.nodes, // Include the nodes array from the example
      },
      name: example.name,
    });
    setViewMode(false);
  };

  // Handle viewing an existing currency
  const handleViewExisting = (currencyData) => {
    const def = currencyData.currencydefinition;
    const typeInfo = getCurrencyTypeInfo(def.options, !!def.nativecurrencyid);
    const typeObj = currencyTypeMap[typeInfo.id] || currencyTypeMap.token;
    
    // For PBaaS currencies, find the bridge converter using gatewayconverterid
    let bridgeConverterData = null;
    if (def.gatewayconverterid) {
      // Search in both currency files for the bridge converter
      const allCurrencyData = [...allCurrencies, ...importedCurrencies];
      const bridgeCurrency = allCurrencyData.find(
        c => c.currencydefinition?.currencyid === def.gatewayconverterid
      );
      if (bridgeCurrency) {
        bridgeConverterData = bridgeCurrency.currencydefinition;
      }
    }
    
    // Parse the currency data to editor format, passing bridge converter if found
    const editorFormat = parseCurrencyToEditorFormat(currencyData, bridgeConverterData);
    
    setSelectedType(typeObj);
    setPreloadedData({
      isExisting: true,
      config: editorFormat,
      name: def.name,
      fullData: currencyData,
      bridgeConverterData,
    });
    setViewMode(true);
  };

  // Reset state when going back
  const handleBack = () => {
    setSelectedType(null);
    setPreloadedData(null);
    setViewMode(false);
  };

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        {!selectedType ? (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            <LandingPage 
              onSelectType={setSelectedType}
              onLoadExample={handleLoadExample}
              onViewExisting={handleViewExisting}
            />
          </motion.div>
        ) : (
          <motion.div
            key="editor"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.3 }}
          >
            <CurrencyEditor 
              currencyType={selectedType} 
              onBack={handleBack}
              preloadedData={preloadedData}
              viewMode={viewMode}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
